import type { Facet } from '../types/Facet';
import type {
  ProductsQueryState,
  SyncProductsQueryFromURLPayload,
  TaxonomyAttribute,
} from '../types/ProductsQueryState';
import { markActiveFacetOptions } from './markActiveFacetOptions';
import { markActiveSortOption } from './markActiveSortOption';

export const TAXONOMY_MULTISELECT_DELIMITER = ';';
export const TAXONOMY_SKIP_SEGMENT = 'all';

export const levelsToPageCategories = (
  facets: Facet[],
  taxonomyAttributeAliases: TaxonomyAttribute[],
): string[] => {
  const facetByAlias = new Map(facets.map((facet) => [facet.alias, facet]));

  const taxonomyLevelValues = taxonomyAttributeAliases.map((alias) => {
    const facet = facetByAlias.get(alias);
    const activeOptions = facet?.options.filter((option) => option.active) ?? [];

    return activeOptions.length > 0
      ? activeOptions
          .map((option) => option.value)
          .sort()
          .join(TAXONOMY_MULTISELECT_DELIMITER)
      : TAXONOMY_SKIP_SEGMENT;
  });

  while (
    taxonomyLevelValues.length > 0 &&
    taxonomyLevelValues[taxonomyLevelValues.length - 1] === TAXONOMY_SKIP_SEGMENT
  ) {
    taxonomyLevelValues.pop();
  }

  return [
    taxonomyLevelValues
      .filter((value) => value !== TAXONOMY_SKIP_SEGMENT)
      .map((value) => value.split(TAXONOMY_MULTISELECT_DELIMITER).join('/'))
      .join(' > '),
  ];
};

export const getProductsQueryStateFromSyncPayload = (
  productsQuery: ProductsQueryState,
  active: SyncProductsQueryFromURLPayload,
): ProductsQueryState => {
  const facets = markActiveFacetOptions(productsQuery.facets, active.activeFacetValuesByKey);

  // For taxonomy pages pageCategories follows the active facet selection; other pages keep
  // whatever was set at SSR-init time (their pathname doesn't change after navigation).
  const pageCategories =
    productsQuery.taxonomyAttributeAliases.length > 0
      ? levelsToPageCategories(facets, productsQuery.taxonomyAttributeAliases)
      : productsQuery.pageCategories;

  return {
    ...productsQuery,
    facets,
    sortOptions: markActiveSortOption(productsQuery.sortOptions, active.activeSort),
    page: active.page,
    searchTerm: active.searchTerm,
    pageCategories,
  };
};
